import React from "react";
import type { RenderVideoNode as RenderVideoNodeModel } from "../../../util/src/layout-compiler/types.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";
import { useProtectedMediaSrc } from "./useProtectedMediaSrc";

type RenderVideoNodeProps = {
  node: RenderVideoNodeModel;
  scale: number;
};

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) return `${rotationTransform} ${extra}`;
  return rotationTransform ?? extra;
}

export function RenderVideoNode({ node, scale }: RenderVideoNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const resolvedSrc = useProtectedMediaSrc(node.src);
  const resolvedPoster = useProtectedMediaSrc(node.poster);
  const clipStyle = getRenderNodeClipStyle(node, scale);

  if (!resolvedSrc) {
    return null;
  }

  return (
    <video
      {...runtimeProps}
      src={resolvedSrc}
      poster={resolvedPoster || undefined}
      muted={node.muted ?? true}
      loop={node.loop ?? true}
      autoPlay={node.autoPlay ?? true}
      controls={node.controls ?? false}
      playsInline
      style={{
        ...(node.style.position === "relative"
          ? {
              position: "relative",
              flexShrink: 0,
            }
          : {
              position: "absolute",
              left: node.x * scale,
              top: node.y * scale,
            }),
        width: node.width * scale,
        height: node.height * scale,
        zIndex: node.zIndex,
        objectFit:
          (node.objectFit as React.CSSProperties["objectFit"]) ?? "contain",
        boxSizing: "border-box",
        ...(node.style as React.CSSProperties),
        transform: composeTransform(node.rotation, node.style?.transform),
        transformOrigin: "center center",
        ...clipStyle,
      }}
    />
  );
}
